import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import RGL, { WidthProvider } from 'react-grid-layout'
import { HEIGHT_ROW, MAX_WIDTH } from '../getGalleryLayout'
import { BlocksHtml, BlocksPipes, ContentBlockGallery, WidgetGallery } from '../../../../../common/types/api/page'
import { GALLERY_CONTENT_MIN_HEIGHT, GALLERY_CONTENT_MIN_WIDTH, getGalleryRowLayout } from './mountGallery'
import { WidgetDescription } from './WidgetDescription'
import { getWidget } from '../../../../../api/getDashboards'
import type { DashboardWidget } from '../../../../../common/types/api/DashboardsResponse'
import { getGalleryContentType } from '../../wrapperDecorator'
import { WidgetTypes } from '../../../../../common/types/api/report'
import { Html } from '../html'
import { Text } from '../text'
import i18n from 'live/services/i18n'
import globalConfirmation from 'live/services/global-confirmation'
import { ItemGridWidgetGallery } from './WidgetGallery'

const GridLayout = WidthProvider(RGL)

export function GalleryRow({
    indexRow,
    column,
    editMode,
    onChangeRow,
    onChange,
    columns,
    openEditor,
    movementIconFocus,
    openMovimentModal,
    key
}) {
    const [infoWidget, setInfoWidget] = useState<{ [key: number]: DashboardWidget }>({})
    const [rowLayout, setRowLayout] = useState(
        getGalleryRowLayout(column, indexRow, editMode, {
            maxHeight: 923,
            maxWidth: MAX_WIDTH,
            id: indexRow,
            lineHeight: HEIGHT_ROW
        })
    )

    useEffect(() => {
        setRowLayout(
            getGalleryRowLayout(column, indexRow, editMode, {
                maxHeight: 923,
                maxWidth: MAX_WIDTH,
                id: indexRow,
                lineHeight: HEIGHT_ROW
            })
        )
    }, [column, indexRow, indexRow, columns, editMode])

    useEffect(() => {
        Promise.all(
            [...column]
                .filter(({ widgetId }) => widgetId !== undefined)
                .map(({ widgetId }) => String(widgetId))
                .map(widgetId => {
                    if (!infoWidget[parseInt(widgetId)]) {
                        return getWidget(parseInt(widgetId))
                    }
                })
                .filter(p => !!p)
        ).then(values => {
            setInfoWidget(cache => ({
                ...cache,
                ...values.reduce((s, w) => ({ ...s, [w.id]: w }), {})
            }))
        })
    }, [column])

    const getContentDescription = (content: WidgetGallery | ContentBlockGallery, indexCol: number): JSX.Element => {
        const contentType = getGalleryContentType(content)
        switch (contentType) {
            case WidgetTypes.GALLERY:
                const widget = content as WidgetGallery
                return <WidgetDescription dashboardWidget={infoWidget[widget.widgetId]} widget={widget} />
            case WidgetTypes.HTML:
                const blockHtml = (content as ContentBlockGallery)?.content as BlocksHtml
                return (
                    <Text
                        type={WidgetTypes.GALLERY}
                        editMode={editMode}
                        className={'gallery-content-preview gallery-html-preview'}
                        {...blockHtml}
                        openEditor={() =>
                            openEditor({
                                indexColumn: indexCol,
                                indexRow: indexRow,
                                widthMax: content.width + currentWidthfreeSpace,
                                contentType: WidgetTypes.HTML
                            })
                        }
                    />
                )
            case WidgetTypes.PIPES:
                const blockPipes = (content as ContentBlockGallery)?.content as BlocksPipes
                return (
                    <Html
                        className={'gallery-content-preview gallery-html-preview'}
                        {...blockPipes}
                        type={WidgetTypes.GALLERY}
                        openEditor={() =>
                            openEditor({
                                indexColumn: indexCol,
                                indexRow: indexRow,
                                widthMax: content.width + currentWidthfreeSpace,
                                contentType: WidgetTypes.PIPES
                            })
                        }
                        editMode={editMode}
                    />
                )
            default:
                break
        }
    }

    function confirmDeletion(callbackDelete: () => void): void {
        const content = i18n('Are you sure you want to delete this content from the email?')
        globalConfirmation.confirm(content, callbackDelete)
    }
    const currentWidthfreeSpace = MAX_WIDTH - rowLayout.reduce((s, { widget }) => s + widget.width, 0)

    
    return (
        <div key={`gallery-collum-${indexRow}`} className='--gallery-row'  id={key}>
            <StyledRow editMode={editMode}>
                {Object.keys(columns).length > 1 ? <label>ROW {indexRow + 1} </label> : null}
            </StyledRow>
            <StyledGallery
                editMode={editMode}
                minHeight={GALLERY_CONTENT_MIN_HEIGHT}
                minWidth={GALLERY_CONTENT_MIN_WIDTH}
            >
                <GridLayout
                    autoSize={true}
                    rowHeight={HEIGHT_ROW}
                    cols={MAX_WIDTH}
                    isResizable={true}
                    width={MAX_WIDTH}
                    isDraggable={false}
                    layout={rowLayout.map(({ grid }) => grid)}
                    useCSSTransforms={true}
                    ItemG
                    compactType={'horizontal'}
                    onResizeStop={(gridLayoutArray, resizedCell) => {
                        try {
                            const resizedItem = gridLayoutArray.find(gridCell => gridCell.i === resizedCell.i)
                            const splitId = resizedItem.i.split('-')

                            const columnIndex = parseInt(splitId[2])

                            const getWToWidth = (): number =>
                                Math.floor(
                                    (MAX_WIDTH * Math.min(resizedItem.w, rowLayout[columnIndex].grid.maxW)) /
                                        GALLERY_CONTENT_MIN_WIDTH /
                                        10
                                ) * 10

                            const widthUsedpsace = rowLayout.reduce((s, { widget }) => s + widget.width, 0)

                            const widget = rowLayout[columnIndex].widget

                            const widthFreespsace = MAX_WIDTH - widthUsedpsace

                            const getHToHeight = (): number => {
                                return Math.round((resizedItem.h * 100) / HEIGHT_ROW)
                            }

                            const widgetGallery = {
                                ...column[columnIndex],
                                height: getHToHeight(),
                                width: Math.min(widget.width + widthFreespsace, resizedItem.w)
                            } as WidgetGallery

                            const newColumns = [...column]
                            newColumns[columnIndex] = widgetGallery
                            onChangeRow(
                                getGalleryRowLayout(newColumns, indexRow, editMode, {
                                    maxHeight: 923,
                                    maxWidth: MAX_WIDTH,
                                    id: indexRow,
                                    lineHeight: HEIGHT_ROW
                                }).map(({ widget }) => widget)
                            )
                        } catch (e) {
                            console.error(e)
                        }
                    }}
                >
                    {rowLayout.map(({ grid, widget }, indexCol) => {
                        return (
                            <div
                                className={`widgetGallery--grid__widgetContainer`}
                                style={{                                    
                                    zIndex: 0,
                                    position: 'absolute'
                                }}
                                key={grid.i}
                                id={`row-${indexCol}`}
                                data-grid={grid}
                            >
                                <>
                                    {editMode && (
                                        <ItemGridWidgetGallery
                                            movementIconIsFocus={movementIconFocus?.row == indexRow && movementIconFocus?.col == indexCol}
                                            openMovimentModal={(el) => openMovimentModal(el,indexRow,indexCol)}
                                            deleteWidget={() => {
                                                confirmDeletion(() => {
                                                    if (columns[indexRow].length === 1) {
                                                        onChange(columns.filter((c, i) => i !== indexRow))
                                                    } else {
                                                        const newC = [...columns]
                                                        newC[indexRow] = columns[indexRow].filter(
                                                            (r, i) => i !== indexCol
                                                        )
                                                        onChange(newC)
                                                    }
                                                })
                                            }}
                                            openEditor={() => {
                                                const contentType = getGalleryContentType(widget)
                                                openEditor({
                                                    indexColumn: indexCol,
                                                    indexRow: indexRow,
                                                    widthMax: widget.width + currentWidthfreeSpace,
                                                    subContentType: contentType
                                                })
                                            }}
                                        />
                                    )}
                                    {getContentDescription(widget, indexCol)}
                                </>
                            </div>
                        )
                    })}
                </GridLayout>
            </StyledGallery>
        </div>
    )
}

const StyledRow = styled.div<{ editMode: boolean }>`
    padding: 8px 0px 8px 16px;    
    label {
        color: var(--palette-light-mode-gray-500, #bac0cb);
        font-size: 13px;
        font-weight: 600;
        line-height: 16px;
    }
    > div {
        position: initial;        
    }
`

const StyledGallery = styled.div<{ editMode: boolean; minWidth: number; minHeight: number }>`
    overflow: initial;
    position: inherit;    
    min-height: ${({ minHeight }) => minHeight}px;
    min-width: ${({ minWidth }) => minWidth}px;
    padding: 0 16px;
    /* height: fit-content; */
    .react-grid-layout {
        opacity: 1;        
        /* position: inherit !important; */
        /* display:inline-block; */
    }
    .react-resizable {
        
    }
    .gallery-html-preview {
        width: 100%;
        margin: 0px;        
        overflow: auto;
        max-height: fit-content;
    }
    .widgetGallery {
        &--placeholder {
            font-size: 20px;
            font-family: ${({ theme }) => theme['font-semibold']};
            color: ${({ theme }) => theme['gray-400']};
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }
        &--grid {
            &__widgetContainer {
                background: ${({ theme, editMode }) => (editMode ? theme['gray-100'] : theme.white)} !important;
                border: 1px solid ${({ theme, editMode }) => (editMode ? theme['gray-100'] : theme['gray-400'])};
                border-radius: 4px;
                &:hover {
                    cursor: ${({ theme, editMode }) => (editMode ? 'pointer' : 'default')};
                    background-color: ${({ theme, editMode }) => (editMode ? theme['blue-200'] : 'none')} !important;
                    .gallery-content-preview,
                    .gallery-content-preview > div {
                        cursor: ${({ theme, editMode }) => (editMode ? 'pointer' : 'default')};
                        background-color: ${({ theme, editMode }) =>
                            editMode ? theme['blue-200'] : 'none'} !important;
                    }
                }
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                overflow-y: auto;
                overflow: visible;
                min-height: ${({ minHeight }) => minHeight}px;
                min-width: ${({ minWidth }) => minWidth}px;
                /* border: solid 1px #c8ccd4; */
                padding: 10px;
                &--title {
                    font-size: 16px;
                    margin-bottom: 6px;
                    font-family: ${({ theme }) => theme['font-semibold']};
                    color: ${({ theme }) => theme['gray-800']};
                }
                &--addBtn {
                    font-size: 13px;
                    padding: 8px 16px;
                    line-height: 13px;
                    border: none;
                    background: ${({ theme }) => theme['blue-500']};
                    color: ${({ theme }) => theme['gray-0']};
                    border-radius: 3px;
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                }
                &--actions {
                    position: absolute;
                    top: 0;
                    right: 10px;
                    transform: translateY(-50%);
                    display: flex;
                    justify-content: flex-end;
                    gap: 4px;
                }
            }
        }
        &--footerContainer {
            height: 50px;
            width: 100%;
            display: flex;
            justify-content: center;
            &__addContentContainer {
                display: flex;
                width: 300px;
                height: 100%;
                border: 1px solid green;
                gap: 16px;
                &--buttonContainer {
                }
                &--checkboxContainer {
                    flex-grow: 1;
                }
            }
        }
    }
`
